import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { getAuth } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { Checkbox } from '../../components/ui/checkbox';
import ReprocessMenu from '../shared/ReprocessMenu';
import {
  Upload, Loader2, FileText, CheckCircle2, XCircle, Clock, Eye,
  Building2, ChevronsDown, Zap, RefreshCw, ScanLine, Download,
  Trash2, Search, X, Check, ChevronDown,
} from 'lucide-react';

const fmtDate = (v) => {
  if (!v) return '\u2014';
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const statusCfg = {
  queued: { label: 'Queued', icon: Clock, cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  processing: { label: 'Processing', icon: Loader2, cls: 'bg-blue-100 text-blue-700 border-blue-200', spin: true },
  ocr_extracting: { label: 'OCR', icon: ScanLine, cls: 'bg-amber-100 text-amber-700 border-amber-200', spin: true },
  ai_parsing: { label: 'AI Parsing', icon: Zap, cls: 'bg-purple-100 text-purple-700 border-purple-200', spin: true },
  mysql_matching: { label: 'Matching', icon: RefreshCw, cls: 'bg-cyan-100 text-cyan-700 border-cyan-200', spin: true },
  completed: { label: 'Completed', icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  failed: { label: 'Failed', icon: XCircle, cls: 'bg-red-100 text-red-700 border-red-200' },
};

function StatusBadge({ status }) {
  const c = statusCfg[status] || statusCfg.queued;
  const Icon = c.icon;
  return <Badge variant="outline" className={`text-[10px] font-semibold ${c.cls} h-5 px-2 gap-1`}><Icon className={`h-3 w-3 ${c.spin ? 'animate-spin' : ''}`} />{c.label}</Badge>;
}

function ExNewBadge({ status }) {
  return status === 'existing'
    ? <Badge variant="outline" className="text-[9px] bg-sky-50 text-sky-700 border-sky-200 h-4 px-1.5 font-semibold">Existing</Badge>
    : <Badge variant="outline" className="text-[9px] bg-green-50 text-green-700 border-green-200 h-4 px-1.5 font-semibold">New</Badge>;
}

function ApprovalBadge({ status }) {
  return status === 'approved'
    ? <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200 h-4 px-1.5 font-semibold gap-0.5"><Check className="h-2.5 w-2.5" />Approved</Badge>
    : <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200 h-4 px-1.5 font-semibold">Review</Badge>;
}

export default function Bill2RollinPage() {
  const navigate = useNavigate();
  const [currentAccount, setCurrentAccount] = useState(() => {
    const s = localStorage.getItem('current_account');
    return s ? JSON.parse(s) : null;
  });
  useEffect(() => {
    const check = () => {
      const s = localStorage.getItem('current_account');
      const acc = s ? JSON.parse(s) : null;
      if (JSON.stringify(acc) !== JSON.stringify(currentAccount)) setCurrentAccount(acc);
    };
    const h = setInterval(check, 800);
    window.addEventListener('storage', check);
    return () => { clearInterval(h); window.removeEventListener('storage', check); };
  }, [currentAccount]);
  const accountId = currentAccount?.id;

  const [tab, setTab] = useState('jobs');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [stats, setStats] = useState({});

  const [jobs, setJobs] = useState([]);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsHasMore, setJobsHasMore] = useState(false);
  const jobsPageRef = useRef(1);
  const [selectedJobIds, setSelectedJobIds] = useState(new Set());

  const toggleJobSelect = (jobId) => {
    setSelectedJobIds(prev => {
      const n = new Set(prev);
      if (n.has(jobId)) n.delete(jobId); else n.add(jobId);
      return n;
    });
  };

  const toggleSelectAllVisibleJobs = () => {
    const allIds = jobs.map(j => j.job_id);
    const allSelected = allIds.every(id => selectedJobIds.has(id));
    setSelectedJobIds(allSelected ? new Set() : new Set(allIds));
  };

  const [properties, setProperties] = useState([]);
  const [propsTotal, setPropsTotal] = useState(0);
  const [propsLoading, setPropsLoading] = useState(true);
  const [propsHasMore, setPropsHasMore] = useState(false);
  const propsPageRef = useRef(1);
  const [propFilterStatus, setPropFilterStatus] = useState('');
  const [propFilterApproval, setPropFilterApproval] = useState('');
  const [propSearch, setPropSearch] = useState('');

  const pollRef = useRef(null);

  const loadStats = useCallback(async () => {
    if (!accountId) return;
    try { const { data } = await api.get('/bill2rollin/stats', { params: { account_id: accountId } }); setStats(data); } catch {}
  }, [accountId]);

  const loadJobs = useCallback(async (page = 1) => {
    if (!accountId) return;
    if (page === 1) setJobsLoading(true);
    try {
      const { data } = await api.get('/bill2rollin/jobs', { params: { account_id: accountId, page, limit: 25 } });
      page === 1 ? setJobs(data.jobs || []) : setJobs(prev => [...prev, ...(data.jobs || [])]);
      setJobsTotal(data.total || 0); setJobsHasMore(data.has_more); jobsPageRef.current = page;
    } catch {} finally { setJobsLoading(false); }
  }, [accountId]);

  const loadProperties = useCallback(async (page = 1) => {
    if (!accountId) return;
    if (page === 1) setPropsLoading(true);
    try {
      const params = { account_id: accountId, page, limit: 25 };
      if (propFilterStatus) params.mysql_status = propFilterStatus;
      if (propFilterApproval) params.approval_status = propFilterApproval;
      if (propSearch) params.search = propSearch;
      const { data } = await api.get('/bill2rollin/properties', { params });
      page === 1 ? setProperties(data.properties || []) : setProperties(prev => [...prev, ...(data.properties || [])]);
      setPropsTotal(data.total || 0); setPropsHasMore(data.has_more); propsPageRef.current = page;
    } catch {} finally { setPropsLoading(false); }
  }, [accountId, propFilterStatus, propFilterApproval, propSearch]);

  useEffect(() => { loadStats(); loadJobs(); loadProperties(); }, [loadStats, loadJobs, loadProperties]);

  useEffect(() => {
    const hasActive = jobs.some(j => !['completed', 'failed'].includes(j.status));
    if (hasActive) { pollRef.current = setInterval(() => { loadJobs(); loadProperties(); loadStats(); }, 3000); }
    else if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [jobs, loadJobs, loadProperties, loadStats]);

  const handleUpload = async (e) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    const pdfs = Array.from(fileList).filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (pdfs.length === 0) { toast.error('Select PDF files'); return; }
    setUploading(true);
    const tid = toast.loading(`Uploading ${pdfs.length} bill${pdfs.length > 1 ? 's' : ''}...`);
    try {
      const fd = new FormData();
      pdfs.forEach(f => fd.append('files', f));
      const { data } = await api.post(`/bill2rollin/upload?account_id=${accountId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const ok = data.uploaded || 0;
      const skip = data.skipped || 0;
      toast.success(`${ok} bill${ok !== 1 ? 's' : ''} uploaded${skip ? `, ${skip} skipped` : ''}`, { id: tid });
      loadJobs(); loadStats(); setTab('jobs');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed', { id: tid }); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const rerunJob = async (jobId) => {
    try { await api.post(`/bill2rollin/jobs/${jobId}/rerun?account_id=${accountId}`); toast.success('Job re-queued'); loadJobs(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Re-run failed'); }
  };

  const deleteJob = async (jobId) => {
    if (!window.confirm('Delete this job and all its extracted data?')) return;
    try { await api.delete(`/bill2rollin/jobs/${jobId}?account_id=${accountId}`); toast.success('Job deleted'); loadJobs(); loadProperties(); loadStats(); }
    catch { toast.error('Delete failed'); }
  };

  const deleteProperty = async (propertyId) => {
    if (!window.confirm('Delete this property and its accounts?')) return;
    try { await api.delete(`/bill2rollin/properties/${propertyId}?account_id=${accountId}`); toast.success('Property deleted'); loadProperties(); loadStats(); }
    catch { toast.error('Delete failed'); }
  };

  const downloadExport = async (type = 'common-area') => {
    const tid = toast.loading(`Preparing ${type} export...`);
    try {
      const auth = getAuth();
      const qs = new URLSearchParams({ account_id: accountId });
      if (propFilterApproval) qs.set('approval_status', propFilterApproval);
      const resp = await fetch(`${api.defaults.baseURL}/bill2rollin/export/${type}?${qs}`, {
        headers: auth?.token ? { Authorization: `Bearer ${auth.token}` } : {},
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `${type}_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success('Export downloaded', { id: tid });
    } catch { toast.error('Export failed', { id: tid }); }
  };

  if (!accountId) {
    return <MainLayout><div className="p-8 text-center text-sm text-muted-foreground">Please select an organization.</div></MainLayout>;
  }

  return (
    <MainLayout wide>
      <div className="space-y-4 p-4 md:p-6" data-testid="bill2rollin-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-sm">
              <Zap className="h-4 w-4 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="text-xl font-bold" data-testid="b2r-title">AI Bill2Rollin</h1>
              <p className="text-[11px] text-muted-foreground">Upload PDF bills to auto-discover Properties, Utility Accounts & Meters</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="b2r-export-btn"><Download className="h-3.5 w-3.5 mr-1" /> Export <ChevronDown className="h-3 w-3 ml-1" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => downloadExport('common-area')} data-testid="b2r-export-common">Common Area Accounts</DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadExport('unit-accounts')} data-testid="b2r-export-unit">Unit Accounts</DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadExport('meters')} data-testid="b2r-export-meters">Meters</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <input ref={fileRef} type="file" accept=".pdf" multiple className="hidden" onChange={handleUpload} data-testid="b2r-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-2" data-testid="b2r-upload-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload Bills
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          {[
            { label: 'Properties', value: stats.total_properties, color: 'text-slate-700' },
            { label: 'New Properties', value: stats.new_properties, color: 'text-green-700', bg: 'bg-green-50 border-green-200' },
            { label: 'Approved', value: stats.approved_accounts, color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
            { label: 'Accounts', value: stats.total_accounts, color: 'text-blue-700' },
            { label: 'Meters', value: stats.total_meters, color: 'text-violet-700' },
            { label: 'Units', value: stats.total_units, color: 'text-slate-600' },
            { label: 'Missing Links', value: stats.missing_links, color: 'text-red-700', bg: 'bg-red-50 border-red-200' },
          ].map((k, i) => (
            <div key={i} className={`border rounded-lg p-3 text-center ${k.bg || 'bg-card'}`} data-testid={`b2r-kpi-${i}`}>
              <p className={`text-lg font-bold ${k.color}`}>{k.value ?? '\u2014'}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{k.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b">
          <button onClick={() => setTab('jobs')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'jobs' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-testid="b2r-tab-jobs">
            Job Queue {jobsTotal > 0 && <Badge variant="secondary" className="ml-1.5 text-[10px] h-4 px-1.5">{jobsTotal}</Badge>}
          </button>
          <button onClick={() => setTab('rollin')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'rollin' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-testid="b2r-tab-rollin">
            Roll In {propsTotal > 0 && <Badge variant="secondary" className="ml-1.5 text-[10px] h-4 px-1.5">{propsTotal}</Badge>}
          </button>
        </div>

        {/* Job Queue */}
        {tab === 'jobs' && (
          <div className="space-y-2" data-testid="b2r-jobs-panel">
            <div className="flex items-center justify-end gap-2">
              <ReprocessMenu
                accountId={accountId}
                selectedIds={Array.from(selectedJobIds)}
                onDone={() => { setSelectedJobIds(new Set()); loadJobs(1); }}
              />
            </div>
            <div className="border rounded-md overflow-x-auto">
            <Table className="w-full min-w-[1100px]">
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  <TableHead className="w-[32px] pl-2">
                    <Checkbox
                      checked={jobs.length > 0 && jobs.every(j => selectedJobIds.has(j.job_id))}
                      onCheckedChange={toggleSelectAllVisibleJobs}
                      aria-label="Select all"
                      data-testid="b2r-select-all-jobs"
                    />
                  </TableHead>
                  <TableHead className="text-xs font-semibold w-[40px]">#</TableHead>
                  <TableHead className="text-xs font-semibold w-[210px]">File Name</TableHead>
                  <TableHead className="text-xs font-semibold w-[100px]">Status</TableHead>
                  <TableHead className="text-xs font-semibold w-[130px]">Property</TableHead>
                  <TableHead className="text-xs font-semibold w-[55px] text-center">Accts</TableHead>
                  <TableHead className="text-xs font-semibold w-[55px] text-center">Mtrs</TableHead>
                  <TableHead className="text-xs font-semibold w-[40px] text-center">Pg</TableHead>
                  <TableHead className="text-xs font-semibold w-[60px]">Via</TableHead>
                  <TableHead className="text-xs font-semibold w-[100px]">Uploaded By</TableHead>
                  <TableHead className="text-xs font-semibold w-[130px]">Date</TableHead>
                  <TableHead className="text-xs font-semibold w-[100px] text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobsLoading ? (
                  Array.from({ length: 5 }).map((_, i) => <TableRow key={i}>{Array.from({ length: 12 }).map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>)
                ) : jobs.length === 0 ? (
                  <TableRow><TableCell colSpan={12} className="text-center py-16 text-muted-foreground"><Upload className="h-8 w-8 mx-auto mb-2 opacity-40" />No bills uploaded yet.</TableCell></TableRow>
                ) : jobs.map((j, i) => (
                  <TableRow key={j.job_id} className="hover:bg-muted/20" data-testid={`b2r-job-row-${i}`}>
                    <TableCell className="pl-2">
                      <Checkbox
                        checked={selectedJobIds.has(j.job_id)}
                        onCheckedChange={() => toggleJobSelect(j.job_id)}
                        aria-label="Select job"
                        data-testid={`b2r-select-job-${i}`}
                      />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                    <TableCell className="text-xs"><div className="flex items-center gap-2"><FileText className="h-4 w-4 text-red-500 shrink-0" /><span className="font-medium truncate" title={j.file_name}>{j.file_name}</span></div></TableCell>
                    <TableCell><StatusBadge status={j.status} /></TableCell>
                    <TableCell className="text-xs truncate" title={j.property_name}>{j.property_name || '\u2014'}</TableCell>
                    <TableCell className="text-xs text-center font-mono">{j.accounts_count ?? '\u2014'}</TableCell>
                    <TableCell className="text-xs text-center font-mono">{j.meters_count ?? '\u2014'}</TableCell>
                    <TableCell className="text-xs text-center font-mono">{j.total_pages ?? '\u2014'}</TableCell>
                    <TableCell className="text-xs">
                      <Badge variant="outline" className={`text-[9px] h-4 px-1 ${j.dispatch === 'sqs' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                        {j.dispatch === 'sqs' ? 'SQS' : 'Local'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground truncate">{j.uploaded_by || '\u2014'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(j.date_uploaded)}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-0.5">
                        {j.status !== 'processing' && j.status !== 'queued' && (
                          <Button variant="outline" size="sm" className="h-6 text-[10px] px-1.5 gap-0.5" onClick={() => rerunJob(j.job_id)} data-testid={`b2r-rerun-${i}`}>
                            <RefreshCw className="h-3 w-3" /> Re-run
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={() => deleteJob(j.job_id)} data-testid={`b2r-del-job-${i}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {jobsHasMore && <div className="flex justify-center py-3 border-t"><Button variant="outline" size="sm" onClick={() => loadJobs(jobsPageRef.current + 1)}><ChevronsDown className="h-3.5 w-3.5 mr-1" /> Load more</Button></div>}
            </div>
          </div>
        )}

        {/* Roll In */}
        {tab === 'rollin' && (
          <>
            <div className="border rounded-lg p-3 bg-card">
              <div className="flex flex-wrap items-center gap-2">
                <Select value={propFilterStatus} onValueChange={(v) => setPropFilterStatus(v === 'all' ? '' : v)}>
                  <SelectTrigger className="h-8 w-36 text-xs"><SelectValue placeholder="All Status" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="new">New</SelectItem><SelectItem value="existing">Existing</SelectItem></SelectContent>
                </Select>
                <Select value={propFilterApproval} onValueChange={(v) => setPropFilterApproval(v === 'all' ? '' : v)}>
                  <SelectTrigger className="h-8 w-36 text-xs"><SelectValue placeholder="All Approval" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All Approval</SelectItem><SelectItem value="review">Review</SelectItem><SelectItem value="approved">Approved</SelectItem></SelectContent>
                </Select>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                  <Input value={propSearch} onChange={(e) => setPropSearch(e.target.value)} placeholder="Search property..." className="h-8 w-44 text-xs pl-6" />
                </div>
                {(propFilterStatus || propFilterApproval || propSearch) && (
                  <Button variant="ghost" size="sm" onClick={() => { setPropFilterStatus(''); setPropFilterApproval(''); setPropSearch(''); }}><X className="h-3.5 w-3.5 mr-1" /> Clear</Button>
                )}
                <span className="ml-auto text-xs text-muted-foreground">Showing <strong>{properties.length}</strong> of <strong>{propsTotal}</strong></span>
              </div>
            </div>
            <div className="border rounded-md overflow-x-auto" data-testid="b2r-rollin-panel">
              <Table className="w-full min-w-[1200px]">
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow>
                    <TableHead className="text-xs font-semibold w-[40px]">#</TableHead>
                    <TableHead className="text-xs font-semibold w-[180px]">Property</TableHead>
                    <TableHead className="text-xs font-semibold w-[160px]">Bill (File)</TableHead>
                    <TableHead className="text-xs font-semibold w-[70px]">Status</TableHead>
                    <TableHead className="text-xs font-semibold w-[70px]">Approval</TableHead>
                    <TableHead className="text-xs font-semibold w-[60px] text-center">Accts</TableHead>
                    <TableHead className="text-xs font-semibold w-[55px] text-center">Units</TableHead>
                    <TableHead className="text-xs font-semibold w-[55px] text-center">Mtrs</TableHead>
                    <TableHead className="text-xs font-semibold w-[35px] text-center">Pg</TableHead>
                    <TableHead className="text-xs font-semibold w-[110px]">Created</TableHead>
                    <TableHead className="text-xs font-semibold w-[90px]">By</TableHead>
                    <TableHead className="text-xs font-semibold w-[90px] text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {propsLoading ? (
                    Array.from({ length: 5 }).map((_, i) => <TableRow key={i}>{Array.from({ length: 12 }).map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>)
                  ) : properties.length === 0 ? (
                    <TableRow><TableCell colSpan={12} className="text-center py-16 text-muted-foreground"><Building2 className="h-8 w-8 mx-auto mb-2 opacity-40" />No properties found.</TableCell></TableRow>
                  ) : properties.map((p, i) => (
                    <TableRow key={p.property_id} className="hover:bg-muted/20" data-testid={`b2r-prop-row-${i}`}>
                      <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                      <TableCell className="text-xs">
                        <div className="flex flex-col gap-0.5">
                          <span className="font-medium">{p.name}</span>
                          {(p.city || p.state) && <span className="text-[10px] text-muted-foreground">{[p.city, p.state].filter(Boolean).join(', ')}</span>}
                          {p.mysql_site_id && <span className="text-[9px] font-mono text-sky-600">Linked: {String(p.mysql_site_id).slice(0, 16)}...</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs"><div className="flex items-center gap-1.5"><FileText className="h-3.5 w-3.5 text-red-500 shrink-0" /><span className="truncate" title={p.file_name}>{p.file_name || '\u2014'}</span></div></TableCell>
                      <TableCell><ExNewBadge status={p.mysql_status || 'new'} /></TableCell>
                      <TableCell><ApprovalBadge status={p.approval_status || 'review'} /></TableCell>
                      <TableCell className="text-xs text-center font-mono font-semibold">{p.accounts_count}</TableCell>
                      <TableCell className="text-xs text-center font-mono">{p.units_count}</TableCell>
                      <TableCell className="text-xs text-center font-mono">{p.meters_count}</TableCell>
                      <TableCell className="text-xs text-center font-mono text-violet-600">{p.page_ref || '\u2014'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(p.created_at)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground truncate">{p.uploaded_by || '\u2014'}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-0.5">
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => navigate(`/ubm/bill2rollin/property/${p.property_id}`)} data-testid={`b2r-view-btn-${i}`}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={() => deleteProperty(p.property_id)} data-testid={`b2r-del-prop-${i}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {propsHasMore && <div className="flex justify-center py-3 border-t"><Button variant="outline" size="sm" onClick={() => loadProperties(propsPageRef.current + 1)}><ChevronsDown className="h-3.5 w-3.5 mr-1" /> Load more</Button></div>}
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
}

Bill2RollinPage.displayName = 'Bill2RollinPage';
